interface IMapColors {
    value: string;
    fillColor: Array<number>;
    outlineColor?: string;
}

const DefaultContactCategoryMapColors: Array<IMapColors> = [
    // LEASED / SIGNED / IN-HAND (Compact: #FFFF50)
    { value: 'LEASED (SIGNED)', fillColor: [255, 255, 80, 0.5] },
    { value: 'Leased', fillColor: [255, 255, 80, 0.5] },
    { value: 'In-Hand', fillColor: [255, 255, 192, 0.5] },
    { value: 'In-Handed', fillColor: [255, 255, 192, 0.5] },
    // Committed (#00FFC5)
    { value: 'Committed', fillColor: [0, 255, 197, 0.5] },
    // Active Negotiation (Compact: #73DFFF)
    { value: 'Negotiating', fillColor: [115, 223, 255, 0.5] },
    { value: 'Wants Better Terms', fillColor: [223, 115, 255, 0.5] },
    { value: 'Working', fillColor: [255, 235, 190, 0.5] },
    { value: 'Professional', fillColor: [204, 0, 102, 0.5] },
    { value: 'Mailed Letter Offer', fillColor: [180, 220, 240, 0.5] },
    // Hold (#E31A1C)
    { value: 'Hold', fillColor: [227, 26, 28, 0.5] },
    // Leased to Others (Compact: #FBAF99)
    { value: 'Leased to Others', fillColor: [251, 154, 153, 0.5] },
    { value: 'HBP (Held By Production)', fillColor: [204, 78, 92, 0.5] },
    { value: 'HBP', fillColor: [204, 78, 92, 0.5] },
    // Will Not Lease (#E79F79)
    { value: 'Will Not Lease', fillColor: [231, 159, 121, 0.5] },
    // Open (#F0F0F0)
    { value: 'Open', fillColor: [240, 240, 240, 0.5] },
    { value: 'Default', fillColor: [240, 240, 240, 0.5] },
];

/*
 * Contact Category whitelist. Only these labels get their own color and legend row;
 * every other raw Contact_Category value buckets to "Other" (light grey).
 *
 * 'Leased' is not a category a user picks off the Contact Category dropdown for most
 * ownerships — in-handing a lease CLEARS the ownership's contact category (see
 * LeaseService.UpdateLeaseInHandStatus) and moves the signal to Ownership.LeasedStatus.
 * dbo.usp_GISMapTractAttributes, which the enricher bakes into the tracts GeoJSON, reads
 * 'Leased' for those ownerships, so a leased interest still shows on this layer instead of
 * silently contributing nothing. A handful of ownerships also carry a literal 'Leased'
 * contact category; both mean the same thing and share this bucket.
 */
const CONTACT_CATEGORY_WHITELIST: Array<string> = [
    'Committed',
    'Negotiating',
    'Working',
    'Leased',
    'Will Not Lease',
    'Wants Better Terms',
    'Leased to Others',
    'Mailed Letter Offer',
    'Professional',
];

/*
 * Legend display order, per the DLC Lease Contact standard (DLC_COLOR_STANDARDS.md, 9-category):
 * In-Hand, Committed, Negotiating, Wants Better Terms, Working, Professional, Hold, Will Not Lease,
 * Leased to Others. 'Leased' is this app's In-Hand bucket. 'Hold' isn't whitelisted, so it has no
 * row; 'Mailed Letter Offer' isn't in the standard and goes last. Display order only — colors are
 * still resolved in CONTACT_CATEGORY_WHITELIST order so auto-assigned palettes don't shift.
 */
const CONTACT_CATEGORY_LEGEND_ORDER: Array<string> = [
    'Leased',
    'Committed',
    'Negotiating',
    'Wants Better Terms',
    'Working',
    'Professional',
    'Will Not Lease',
    'Leased to Others',
    'Mailed Letter Offer',
];
// dbo.usp_GISMapTractAttributes also writes this for an owner nothing holds and no one has given a
// category, so the map shows an owner no one is working.
const CONTACT_CATEGORY_OTHER_LABEL = 'Other';
const CONTACT_CATEGORY_OTHER_COLOR: Array<number> = [200, 200, 200, 1];

/*
 * Fold one raw Contact_Category value to a whitelist label or "Other".
 *
 * The split is on ' - ' (space-hyphen-space) and takes the head, so the many
 * 'Committed - <sub-state>' values collapse to 'Committed' while compound words
 * ('Un-locatable Over 5 Acres', 'Documents In-Hand') survive intact.
 */
const bucketContactPiece = (piece: string): string => {
    const trimmed = (piece || '').trim();
    const dashIdx = trimmed.indexOf(' - ');
    const headText = dashIdx >= 0 ? trimmed.substring(0, dashIdx).trim() : trimmed;
    const key = headText.toLowerCase();
    const matched = CONTACT_CATEGORY_WHITELIST.find((w) => w.toLowerCase() === key);
    return matched || CONTACT_CATEGORY_OTHER_LABEL;
};

/*
 * The renderer key for one feature's raw Contact_Category: fold every pipe-delimited
 * piece, dedupe, rejoin with ' | '. A tract carrying more than one distinct category
 * yields a combo key ('Committed | Working') that getTractContactCategoryInfo has a
 * hatch symbol for; anything else yields a single label with a solid symbol.
 *
 * MUST return exactly what contactCategoryValueExpression() returns for the same input
 * — a mismatch means the feature finds no uniqueValueInfo and silently falls through to
 * the renderer's grey defaultSymbol. Both are derived from CONTACT_CATEGORY_WHITELIST
 * above so they cannot drift, and contactCategory.test.ts pins them together.
 */
const contactCategoryKey = (raw: string): string => {
    const whole = (raw || '').trim();
    if (whole === '') {
        return CONTACT_CATEGORY_OTHER_LABEL;
    }
    const buckets = whole.split('|').map((piece) => bucketContactPiece(piece));
    return buckets.filter((bucket, i) => buckets.indexOf(bucket) === i).join(' | ');
};

/*
 * The same fold, as Arcade, for the layer's renderer — generated from the whitelist so
 * the map and the legend cannot drift apart.
 *
 * Identifier names here are load-bearing. Arcade treats identifiers case-insensitively,
 * so `var lower = Lower(head)` does not shadow the built-in Lower(), it COLLIDES with
 * it: the first call resolves to the function, every later one resolves to the string
 * and raises. That is precisely the bug this replaces — the loop survived one iteration
 * and threw on the second, so single-category tracts rendered correctly while every
 * multi-category tract returned null and fell through to the grey default, i.e. the
 * hatch symbols were built and were never once used. Keep variable names distinct in
 * shape from every Arcade built-in (Lower/Upper/Find/Left/Trim/Replace/Split/Text/...).
 */
const contactCategoryValueExpression = (): string => {
    const bucketChain = CONTACT_CATEGORY_WHITELIST.map(
        (label, i) => `${i === 0 ? 'if' : 'else if'} (key == '${label.toLowerCase()}') { return '${label}'; }`
    ).join('\n                ');

    return `
        // Fold one piece to a whitelist label. Mirrors bucketContactPiece() in
        // DefaultContactCategoryMapColors.ts — same ' - ' head split, same lowercasing.
        function BucketOne(piece) {
            var t = Trim(piece);
            if (t == '') { return '${CONTACT_CATEGORY_OTHER_LABEL}'; }
            var dashIdx = Find(' - ', t);
            var headText = IIf(dashIdx >= 0, Trim(Left(t, dashIdx)), t);
            var key = Lower(headText);
            ${bucketChain}
            return '${CONTACT_CATEGORY_OTHER_LABEL}';
        }

        var raw = $feature.Contact_Category;
        if (IsEmpty(raw)) { return '${CONTACT_CATEGORY_OTHER_LABEL}'; }
        var whole = Trim(raw);
        if (whole == '') { return '${CONTACT_CATEGORY_OTHER_LABEL}'; }
        if (Find('|', whole) < 0) { return BucketOne(whole); }

        // Pipe-delimited: fold each piece, dedupe, rejoin. Dedupe is done on a string
        // rather than an array because Arcade array mutation proved brittle here.
        var pieces = Split(whole, '|');
        var outStr = '';
        for (var i in pieces) {
            var bucket = BucketOne(pieces[i]);
            var marker = '<' + bucket + '>';
            var haystack = '<' + Replace(outStr, ' | ', '><') + '>';
            if (Find(marker, haystack) < 0) {
                if (outStr == '') { outStr = bucket; }
                else { outStr = outStr + ' | ' + bucket; }
            }
        }
        if (outStr == '') { return '${CONTACT_CATEGORY_OTHER_LABEL}'; }
        return outStr;
    `;
};

export {
    CONTACT_CATEGORY_WHITELIST,
    CONTACT_CATEGORY_LEGEND_ORDER,
    CONTACT_CATEGORY_OTHER_LABEL,
    CONTACT_CATEGORY_OTHER_COLOR,
    bucketContactPiece,
    contactCategoryKey,
    contactCategoryValueExpression,
};
export default DefaultContactCategoryMapColors;



// WEBPACK FOOTER //
// ./src/components/project/sections/GISComponents/DefaultContactCategoryMapColors.ts